/**
 * Client-side applications store.
 *
 * Now uses real API calls instead of localStorage for backend integration.
 * Maintains the same interface for compatibility with existing components.
 */

import * as API from "./api";
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

export type ApplicationStatus = "pending" | "accepted" | "rejected" | "code_sent" | "code_verified" | "registered";

export type PaymentStatus = "unpaid" | "paid";

export const STATUS_LABELS: Record<ApplicationStatus, string> = {
  pending: "Pending review",
  accepted: "Accepted",
  rejected: "Rejected",
  code_sent: "Code sent",
  code_verified: "Code verified",
  registered: "Registered",
};

// Labels are defined locally in components to avoid circular import issues

export type Application = {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  ageRange: string;
  location: string;
  track: string;
  background?: string;
  status: ApplicationStatus;
  paymentStatus: PaymentStatus;
  registrationCode?: string;
  submittedAt: string;
  registeredAt?: string;
  documentPath?: string;
};

// Transform API application to store format
function transformFromAPI(app: API.Application): Application {
  // Map database status to frontend status
  const statusMap: Record<string, ApplicationStatus> = {
    'pending_review': 'pending',
    'under_review': 'pending',
    'approved': 'accepted',
    'rejected': 'rejected',
    'code_sent': 'code_sent',
    'registered': 'code_verified', // Registered means code verified, not paid yet
    'payment_pending': 'code_verified',
    'payment_successful': 'code_verified',
    'enrolled': 'registered'
  };

  // Map payment status
  const paymentStatusMap: Record<string, PaymentStatus> = {
    'payment_successful': 'paid',
    'enrolled': 'paid',
  };

  // Handle both direct payment_status field and status-based mapping
  let paymentStatus: PaymentStatus = 'unpaid';
  if (app.payment_status) {
    paymentStatus = app.payment_status as PaymentStatus;
  } else {
    paymentStatus = paymentStatusMap[app.status] || 'unpaid';
  }

  return {
    id: app.id?.toString() || '',
    fullName: app.full_name || '',
    email: app.email || '',
    phone: app.phone || '',
    ageRange: app.age_range || '',
    location: app.location || '',
    track: app.track || '',
    background: app.background || '',
    status: statusMap[app.status] || 'pending',
    paymentStatus: paymentStatus,
    registrationCode: app.application_number || undefined, // Use application_number as registration code
    submittedAt: app.submitted_at || '',
    registeredAt: app.registered_at,
    documentPath: app.document_path || undefined,
  };
}

// Transform store application to API format
function transformToAPI(app: Application): any {
  // Map frontend status to database status
  const statusMap: Record<ApplicationStatus, string> = {
    'pending': 'pending_review',
    'accepted': 'approved',
    'rejected': 'rejected',
    'code_sent': 'code_sent',
    'code_verified': 'payment_pending', // Code verified but not paid yet
    'registered': 'enrolled' // Fully registered and enrolled
  };

  return {
    full_name: app.fullName,
    email: app.email,
    phone: app.phone,
    ageRange: app.ageRange,
    location: app.location,
    track: app.track,
    background: app.background,
    status: statusMap[app.status] || 'pending_review',
  };
}

// In-memory cache for applications
let cachedApplications: Application[] = [];
let subscribers: (() => void)[] = [];

function notifySubscribers() {
  subscribers.forEach(cb => cb());
}

export function listApplications(): Application[] {
  return cachedApplications.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
}

// Load applications from API
export async function loadApplications(): Promise<void> {
  try {
    const apiApplications = await API.getApplications();
    cachedApplications = apiApplications.map(transformFromAPI);
    notifySubscribers();
  } catch (error) {
    console.error('Failed to load applications:', error);
    // Fallback to empty list on error - don't notify to prevent crashes
    cachedApplications = [];
  }
}

export async function addApplication(formData: FormData): Promise<Application> {
  try {
    // Submit to API with FormData for file upload
    const response = await API.submitApplication(formData);
    const newApplication: Application = {
      id: response.id.toString(),
      fullName: formData.get('fullName') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      ageRange: formData.get('ageRange') as string,
      location: formData.get('location') as string,
      track: formData.get('track') as string,
      background: formData.get('background') as string,
      status: "pending",
      paymentStatus: "unpaid",
      submittedAt: new Date().toISOString(),
    };
    // Reload applications to get the server-side version
    loadApplications();
    return newApplication;
  } catch (error: any) {
    console.error('Failed to submit application:', error);
    // Re-throw the error so the caller can handle it
    throw error;
  }
}

export function generateCode(): string {
  // Generate a more secure, longer code
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789@#$";
  let out = "";
  for (let i = 0; i < 12; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return `RL-${out}`;
}

export function sendRegistrationCode(id: string): Promise<string | null> {
  const app = cachedApplications.find((a) => a.id === id);
  if (!app) return Promise.resolve(null);
  
  // Prevent sending codes to applications that are already rejected, paid, or registered
  if (app.status === 'rejected' || app.status === 'registered' || app.paymentStatus === 'paid') {
    console.warn('Cannot send code to application that is rejected, paid, or already registered');
    return Promise.resolve(null);
  }
  
  // Update local cache immediately to show loading state
  const updatedApps = cachedApplications.map(a => 
    a.id === id ? { ...a, status: 'code_sent' } : a
  );
  cachedApplications = updatedApps;
  notifySubscribers();
  
  // Generate code via API and get the server-generated code
  return API.setRegistrationCode(parseInt(id), generateCode())
    .then((response) => {
      // Reload to get server confirmation with the actual code
      return loadApplications().then(() => {
        const updatedApp = cachedApplications.find((a) => a.id === id);
        return updatedApp?.registrationCode || null;
      });
    })
    .catch((error) => {
      console.error('Failed to set registration code:', error);
      // Revert local changes on error
      const revertedApps = cachedApplications.map(a => 
        a.id === id ? { ...a, status: app.status } : a
      );
      cachedApplications = revertedApps;
      notifySubscribers();
      throw error;
    });
}

export function setStatus(id: string, status: ApplicationStatus) {
  const app = cachedApplications.find((a) => a.id === id);
  if (!app) return;
  
  // Prevent status changes for applications that are already registered
  if (app.status === 'registered') {
    console.warn('Cannot change status for application that is already registered');
    return;
  }
  
  const oldStatus = app.status;
  
  // Update local cache immediately
  const updatedApps = cachedApplications.map(a => 
    a.id === id ? { ...a, status } : a
  );
  cachedApplications = updatedApps;
  notifySubscribers();
  
  // Update via API in background
  API.updateApplicationStatus(parseInt(id), status)
    .then(() => {
      // Reload to get server confirmation
      loadApplications();
    })
    .catch((error) => {
      console.error('Failed to update status:', error);
      // Revert local changes on error
      const revertedApps = cachedApplications.map(a => 
        a.id === id ? { ...a, status: oldStatus } : a
      );
      cachedApplications = revertedApps;
      notifySubscribers();
    });
}

export function acceptApplication(id: string): Promise<boolean> {
  const app = cachedApplications.find((a) => a.id === id);
  if (!app) return Promise.resolve(false);
  
  if (app.status === 'rejected' || app.status === 'registered') {
    console.warn('Cannot accept application that is rejected or already registered');
    return Promise.resolve(false);
  }
  
  const oldStatus = app.status;
  
  // Update local cache immediately
  const updatedApps = cachedApplications.map(a => 
    a.id === id ? { ...a, status: 'accepted' } : a
  );
  cachedApplications = updatedApps;
  notifySubscribers();
  
  // Update via API
  return API.updateApplicationStatus(parseInt(id), 'accepted')
    .then(() => {
      // Reload to get server confirmation
      return loadApplications().then(() => true);
    })
    .catch((error) => {
      console.error('Failed to accept application:', error);
      // Revert local changes on error
      const revertedApps = cachedApplications.map(a => 
        a.id === id ? { ...a, status: oldStatus } : a
      );
      cachedApplications = revertedApps;
      notifySubscribers();
      throw error;
    });
}

export function rejectApplication(id: string): Promise<boolean> {
  const app = cachedApplications.find((a) => a.id === id);
  if (!app) return Promise.resolve(false);
  
  if (app.status === 'registered') {
    console.warn('Cannot reject application that is already registered');
    return Promise.resolve(false);
  }
  
  if (app.paymentStatus === 'paid') {
    console.warn('Cannot reject application that is already paid');
    return Promise.resolve(false);
  }
  
  const oldStatus = app.status;
  
  // Update local cache immediately
  const updatedApps = cachedApplications.map(a => 
    a.id === id ? { ...a, status: 'rejected' } : a
  );
  cachedApplications = updatedApps;
  notifySubscribers();
  
  // Update via API
  return API.updateApplicationStatus(parseInt(id), 'rejected')
    .then(() => {
      // Reload to get server confirmation
      return loadApplications().then(() => true);
    })
    .catch((error) => {
      console.error('Failed to reject application:', error);
      // Revert local changes on error
      const revertedApps = cachedApplications.map(a => 
        a.id === id ? { ...a, status: oldStatus } : a
      );
      cachedApplications = revertedApps;
      notifySubscribers();
      throw error;
    });
}

export function findByCode(code: string): Application | undefined {
  const normalized = code.trim().toUpperCase();
  return cachedApplications.find((a) => a.registrationCode?.toUpperCase() === normalized);
}

export function findByEmailAndCode(email: string, code: string): Application | undefined {
  const normalizedCode = code.trim().toUpperCase();
  const normalizedEmail = email.trim().toLowerCase();
  console.log('Searching for application:', { normalizedEmail, normalizedCode });
  console.log('Available applications:', cachedApplications.map(a => ({ 
    email: a.email.toLowerCase(), 
    code: a.registrationCode?.toUpperCase() 
  })));
  
  const match = cachedApplications.find((a) => 
    a.registrationCode?.toUpperCase() === normalizedCode && 
    a.email.toLowerCase() === normalizedEmail
  );
  
  console.log('Match found:', !!match);
  return match;
}

export function markRegistered(id: string) {
  const app = cachedApplications.find((a) => a.id === id);
  if (!app) return;
  
  const oldStatus = app.status;
  
  // Update local cache immediately
  const updatedApps = cachedApplications.map(a => 
    a.id === id ? { ...a, status: 'registered' } : a
  );
  cachedApplications = updatedApps;
  notifySubscribers();
  
  // Update via API in background
  API.updateApplicationStatus(parseInt(id), 'registered')
    .then(() => {
      // Reload to get server confirmation
      loadApplications();
    })
    .catch((error) => {
      console.error('Failed to mark as registered:', error);
      // Revert local changes on error
      const revertedApps = cachedApplications.map(a => 
        a.id === id ? { ...a, status: oldStatus } : a
      );
      cachedApplications = revertedApps;
      notifySubscribers();
    });
}

export function subscribe(cb: () => void) {
  subscribers.push(cb);
  // Don't call the callback immediately - let the caller decide when to trigger it
  return () => {
    const index = subscribers.indexOf(cb);
    if (index > -1) {
      subscribers.splice(index, 1);
    }
  };
}

// Export real API authentication functions
import { adminLogin as apiLogin, adminLogout as apiLogout, adminLoggedIn as apiLoggedIn, clientSideLogout, getSessionAdmin } from "./api";

export const adminLogin = apiLogin;
export const adminLoggedIn = apiLoggedIn;
export const adminLogout = apiLogout;
export const getCurrentAdmin = getSessionAdmin;
